import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { useActiveShareId } from '../../contexts/ActiveEntitiesContext/ActiveShareIdContext';
import { useIsEnterpriseSubscriber } from '../../hooks/useIsEnterpriseSubscriber';
import { isSameSiteUrl } from '../utils/sameSite';

type InteractiveLiveUrlMedia = { __typename?: string | null } | null;

/**
 * Whether Live Review is available. `v4.interactive_live_url` is the master
 * switch. While `v4.interactive_live_url_enterprise_only` is on, availability
 * additionally requires an ENTERPRISE subscription when creating and viewing
 * assets in the app. Shares can view an existing live asset because creation
 * already enforced the account entitlement.
 */
export function useIsInteractiveLiveUrlFeatureEnabled() {
  const isInteractiveLiveUrlEnabled = useFeatureFlagEnabled(
    'v4.interactive_live_url'
  );
  const isEnterpriseOnly = useFeatureFlagEnabled(
    'v4.interactive_live_url_enterprise_only'
  );
  const shareId = useActiveShareId();
  const isEnterpriseSubscriber = useIsEnterpriseSubscriber();

  if (!isInteractiveLiveUrlEnabled) {
    return false;
  }

  return isEnterpriseOnly ? Boolean(shareId) || isEnterpriseSubscriber : true;
}

/**
 * Whether live-URL auto-size measurement is enabled. Gates the two-pass
 * content measurement that classifies a live URL as fixed vs. responsive and
 * fits it to the viewport. Off falls back to the plain body auto-fit
 * (`onPageContentResize`) with no probe measurement or settling spinner.
 */
export function useIsLiveUrlAutoSizingEnabled() {
  return useFeatureFlagEnabled('v4.interactive_live_url_auto_sizing');
}

export function isInteractiveLiveUrlMedia(
  mediaData: InteractiveLiveUrlMedia | undefined
): boolean {
  return mediaData?.__typename === 'InteractiveLiveMedia';
}

/**
 * Whether a media typename renders through the interactive media viewer —
 * manifest-driven `InteractiveMedia` or live-URL `InteractiveLiveMedia`. Both
 * share the same viewer/viewport code paths.
 */
export function isInteractiveMediaType(
  mediaTypename: string | null | undefined
): boolean {
  return (
    mediaTypename === 'InteractiveMedia' ||
    mediaTypename === 'InteractiveLiveMedia'
  );
}

/**
 * Reads the live URL carried on an interactive-live media object. `url` only
 * exists on some media unions, so guard structurally (own property, string
 * value) before reading. Callers gate on the live-URL feature/type first; this
 * only extracts the value.
 */
export function getLiveUrlFromMediaData(
  mediaData: { __typename?: string | null } | null | undefined
): string | null {
  if (mediaData && Object.hasOwn(mediaData, 'url')) {
    const { url } = mediaData as { url?: unknown };
    if (typeof url === 'string') {
      return url;
    }
  }
  return null;
}

/**
 * Whether an asset typename should be treated as interactive for
 * comment/annotation/viewport behavior. Interactive live assets reuse the
 * interactive viewer, so they must follow the same code paths as
 * `InteractiveAsset` — anywhere that branched on `=== 'InteractiveAsset'`.
 */
export function isInteractiveAssetType(
  assetType: string | null | undefined
): boolean {
  return (
    assetType === 'InteractiveAsset' || assetType === 'InteractiveLiveAsset'
  );
}

/**
 * Whether an asset typename is specifically the interactive live type. Prefer
 * this over the media-based {@link useIsInteractiveLiveUrlAsset} when only the
 * selected asset type is known (e.g. comment cells), since it doesn't depend on
 * the media viewer data being loaded in the current render.
 */
export function isInteractiveLiveAssetType(
  assetType: string | null | undefined
): boolean {
  return assetType === 'InteractiveLiveAsset';
}

export function useIsInteractiveLiveUrlAsset(
  mediaData: InteractiveLiveUrlMedia | undefined
): boolean {
  return (
    useIsInteractiveLiveUrlFeatureEnabled() &&
    isInteractiveLiveUrlMedia(mediaData)
  );
}

/**
 * Strip origin from a live URL so the anchor identifies where on the website
 * the user is (pathname + search + hash), not which deployment they're on.
 * Two visits to the same route on different hostnames stay anchored together,
 * and the existing url-bar UI shows a short, route-shaped string instead of
 * the full URL.
 *
 * Pass-through for non-absolute inputs (e.g. the iframe content script's
 * `'unknown'` sentinel, or anything that fails `new URL()`).
 *
 * Assumes a pre-vetted live URL. Unlike {@link getLiveUrlHostname}, this does
 * not reject non-https schemes — `new URL('javascript:…')` parses and its
 * path would be returned — so never point it at raw, unvalidated input.
 */
export const getLiveUrlAnchorPath = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/**
 * Media state for the page the iframe is on. A page on the live URL's site
 * (same host, a subdomain or a parent domain) gets its comment anchor. An
 * approved page on another site gets no anchor, so nothing can be commented
 * on or matched there, and `liveUrlExternalUrl` keeps its origin and path for
 * display. The query and hash are dropped because media state is saved to
 * localStorage and sign-in pages carry auth parameters in them.
 *
 * Expects a URL the navigation policy allowed (see `isAllowedLiveUrl`), so it
 * always parses.
 */
export const getLiveUrlLocationState = (
  url: string,
  liveUrl: string
): { currentUrlPath: string | null; liveUrlExternalUrl: string | null } => {
  if (isSameSiteUrl(url, liveUrl, liveUrl)) {
    return {
      currentUrlPath: getLiveUrlAnchorPath(url),
      liveUrlExternalUrl: null,
    };
  }
  const { origin, pathname } = new URL(url);
  return { currentUrlPath: null, liveUrlExternalUrl: `${origin}${pathname}` };
};

/**
 * Extracts the hostname of a live URL for display (e.g. in the "external
 * site" notice). Returns null for anything that is not a parseable https
 * URL: live URLs are always https, and this feeds a trust claim, so it must
 * fail closed rather than echo an unparsed, author-controlled string back as
 * if it were a hostname.
 */
export const getLiveUrlHostname = (url: string): string | null => {
  try {
    const { hostname, protocol } = new URL(url);
    return protocol === 'https:' ? hostname : null;
  } catch {
    return null;
  }
};
